import gql from 'graphql-tag';
import { CART_FRAGMENT, ERROR_FRAGMENT, ORDER_FRAGMENT } from './../fragments/index';

/**
 * 获取购物车和预下单信息
 */
export const fetchGetShoppingCartDocument = gql`
  query getShoppingCart(
    $isRemoveMarkUp: Boolean
    $isUseMember: Boolean
    $isUseShoppingCredit: Boolean
  ) {
    getShoppingCart(
      isRemoveMarkUp: $isRemoveMarkUp
      isUseMember: $isUseMember
      isUseShoppingCredit: $isUseShoppingCredit
    ) {
      shoppingTrolley {
        ...Order
      }
      outrightPurchase {
        ...Order
      }
    }
  }
  fragment Order on Order {
    id
    shipping
    customFields {
      orderPromotionResult {
        promResult {
          orderTotalPrice
        }
      }
    }
    lines {
      ...OrderLine
    }
  }

  fragment OrderLine on OrderLine {
    id
    quantity
    unitPrice
    productVariant {
      ...ProductVariant
    }
  }

  fragment ProductVariant on ProductVariant {
    id
    sku
    price
    name
    exclusionGroupsTypeName
    isCanPurchase
    options {
      code
      name
    }
    pointExchange {
      points
    }
    featuredAsset {
      source
    }
    isThereAnyStock
    product {
      ...Product
    }
    customFields {
      virtualTargetType
    }
  }

  fragment Product on Product {
    id
    name
    enabled
    customFields {
      putOnSaleType
      putOnSaleTime
    }
    productTotalStock
    productPurchasePermission {
      id
      productId
      isMembershipPlanPurchase
      membershipPlans {
        id
      }
      guideMembershipPlanId
    }
    memberPriceActivityAmount {
      minDiscount
      maxDiscount
      minMemberPriceAmount
      maxMemberPriceAmount
      memberPriceProductVariant {
        memberPriceAmount
        productVariantId
        memberDiscount
        discountType
      }
    }
    featuredAsset {
      source
    }
    customFields {
      price
      markingPrice
      limitType
      limitCount
      putOnSaleType
      putOnSaleTime
      isVipProduct
      isHiddenCart
      virtualTargetType
    }
    pointsExchangeInfo {
      allowPurchaseAtOriginalPrice
    }
  }
`;

/**
 * 根据类型查询活跃订单
 */
export const fetchActiveOrderByTypeDocument = gql`
  query getActiveOrderByType($type: OrderPurchaseType!, $isRemoveMarkUp: Boolean) {
    getActiveOrderByType(type: $type, isRemoveMarkUp: $isRemoveMarkUp) {
      id
      code
      state
      active
      updatedAt
      orderPlacedAt
      lines {
        id
        quantity
        totalQuantity
        unitPrice
        featuredAsset {
          preview
        }
        productVariant {
          id
          sku
          price
          name
          pointExchange {
            points
            cash
            exchangeTotal
            exchangeCount
          }
          options {
            code
            name
          }
          featuredAsset {
            source
          }
          virtualTarget {
            ... on Coupon {
              id
              cname: name
              cstate: state
              enable
            }
            ... on MembershipPlan {
              id
              name
              state
            }
          }
          product {
            id
            name
            enabled
            customFields {
              isVipProduct
              unit
            }
            featuredAsset {
              source
            }
            customFields {
              price
              markingPrice
              unit
              limitType
              limitCount
              putOnSaleType
              putOnSaleTime
            }
            productTotalStock
          }
          customFields {
            virtualTargetType
          }
        }
        customFields {
          purchasePattern
        }
      }
      totalQuantity
      total
      shipping
      customFields {
        buyType
        orderPromotionResult {
          promResult {
            orderTotalPrice
            orderTotalPoints
          }
        }
      }
    }
  }
`;

/**
 * 获取购物车优惠信息
 */
export const fetchOrderPromotionResultDocument = gql`
  query orderPromotionResult($orderIds: [ID!]!) {
    orderPromotionResult(orderIds: $orderIds) {
      id
      createdAt
      updatedAt
      isCartShowActuallyPaidGift
      isCartShowFullDiscountPresentGift
      isCartShowSelectiveGift
      promResult {
        orderId
        discountAmount
        orderTotalPrice
        orderTotalPoints
        shoppingCreditsClaim
        shoppingCreditsDeduction
        disableShoppingCredits
        promLineResults {
          promInstanceId
          tags
          orderLines {
            orderLineId
            discountCount
            discountAmount
            skuId
            discount
            displayInThisGroup
            points
            cash
          }
          priority
          type
          shouldGroup
          description
          promTime
          promContent
          promOverview
          discountType
          discountAmount
          discountCount
          meetCondition
          superimposeType
          superimposeTypes
          orderLineBlindBoxMap {
            orderLineId
            blindBoxOrderBuyId
            blindBoxOrderBuyPrice
          }
          gifts {
            promInstanceId
            giftType
            items {
              giftId
              productId
              skuId
              count
              name
              giftPrice
              price
              selected
              isAvailable
              minimumStr
              ladderLevel
            }
          }
          coupons {
            couponId
            selected
            price
            autoSelected
          }
        }
        orderLinePromResults {
          orderLineId
          skuId
          count
          price
          discountAmount
          discountDetails {
            promInstanceId
            type
            superimposeType
            superimposeTypes
            discountCount
            discountAmount
          }
        }
        leftOrderLines {
          skuId
          lineId
          count
          price
          discountAmount
        }
        discountByTypes {
          type
          discountAmount
        }
        gifts {
          promInstanceId
          giftType
          items {
            giftId
            productId
            skuId
            count
            name
            giftPrice
            price
            selected
            isAvailable
            minimumStr
          }
        }
        coupons {
          couponId
          selected
          price
        }
        disableMember
      }
    }
  }
`;

/**
 * 清除所有购物车 - 直接购买场景
 */
export const fetchRemoveAllOrderLinesDocument = gql`
  mutation removeAllOrderLines {
    removeAllOrderLines {
      ...Cart
      ...ErrorResult
    }
  }
  ${CART_FRAGMENT}
  ${ERROR_FRAGMENT}
`;

/**
 * 添加至购物车
 */
export const fetchAddItemToOrderByOrderTypeDocument = gql`
  mutation addItemToOrderByOrderType(
    $productVariantId: ID!
    $quantity: Int!
    $type: OrderPurchaseType!
    $buyType: OrderBuyType
  ) {
    addItemToOrderByOrderType(
      productVariantId: $productVariantId
      quantity: $quantity
      type: $type
      buyType: $buyType
    ) {
      ... on Order {
        id
      }
    }
  }
`;

/**
 * 修改购物车规格和数量
 */
export const fetchUpdateShoppingCartSkuDocument = gql`
  mutation updateShoppingCartSku($orderLineId: ID!, $productVariantId: ID!, $quantity: Int!) {
    updateShoppingCartSku(
      orderLineId: $orderLineId
      productVariantId: $productVariantId
      quantity: $quantity
    ) {
      ...Cart
    }
  }
  ${CART_FRAGMENT}
`;

/**
 * 修改指定项购物车数量
 */
export const fetchAdjustOrderLineDocument = gql`
  mutation adjustOrderLineByOrderType(
    $orderLineId: ID!
    $quantity: Int!
    $type: OrderPurchaseType!
  ) {
    adjustOrderLineByOrderType(orderLineId: $orderLineId, quantity: $quantity, type: $type) {
      id
    }
  }
`;

/**
 * 查询商品限购数量
 */
export const fetchGetPurchaseQuantityDocument = gql`
  query getPurchaseQuantity(
    $productId: ID!
    $type: OrderPurchaseType
    $isIncludeCurrentOrder: Boolean
  ) {
    getPurchaseQuantity(
      productId: $productId
      type: $type
      isIncludeCurrentOrder: $isIncludeCurrentOrder
    )
  }
`;

/**
 * 删除指定项购物车
 */
export const fetchRemoveOrderLineDocument = gql`
  mutation removeOrderLineByOrderType($orderLineIds: [ID!]!, $type: OrderPurchaseType!) {
    removeOrderLineByOrderType(orderLineIds: $orderLineIds, type: $type) {
      id
    }
  }
`;

/**
 * 删除全部订单项
 */
export const fetchRemoveAllOrderLinesByOrderTypeDocument = gql`
  mutation removeAllOrderLinesByOrderType($type: OrderPurchaseType!) {
    removeAllOrderLinesByOrderType(type: $type) {
      id
    }
  }
`;

/**
 * 购物车结算
 */
export const fetchShoppingCartSettlementDocument = gql`
  mutation shoppingCartSettlement($input: [SettlementProduct!]!) {
    shoppingCartSettlement(input: $input) {
      id
      lines {
        id
      }
    }
  }
`;

/**
 * 设置下单地址
 */
export const fetchSetShippingAddressDocument = gql`
  mutation setShippingAddress($orderId: ID!, $input: CreateAddressInput!) {
    setShippingAddress(orderId: $orderId, input: $input) {
      ...Cart
      ... on Order {
        shippingAddress {
          ...OrderAddress
        }
      }
    }
  }

  ${CART_FRAGMENT}

  fragment OrderAddress on OrderAddress {
    fullName
    company
    streetLine1
    streetLine2
    city
    province
    postalCode
    country
    phoneNumber
  }
`;

/**
 * 获取发货方式
 */
export const fetchEligibleShippingMethodsByOrderTypeDocument = gql`
  query eligibleShippingMethodsByOrderType($type: OrderPurchaseType!) {
    eligibleShippingMethodsByOrderType(type: $type) {
      id
      name
      description
      price
      priceWithTax
      metadata
    }
  }
`;

/**
 * 设置发货方式
 */
export const setShippingMethodByOrderTypeDocument = gql`
  mutation setShippingMethodByOrderType($type: OrderPurchaseType!, $shippingMethodId: ID!) {
    setShippingMethodByOrderType(type: $type, shippingMethodId: $shippingMethodId) {
      id
    }
  }
`;

/**
 * 设置下单自定义字段
 */
export const fetchSetOrderCustomFieldsDocument = gql`
  mutation setOrderCustomFields($input: UpdateOrderInput!) {
    setOrderCustomFields(input: $input) {
      ...Order
      ...Cart
      ...ErrorResult
    }
  }
  ${CART_FRAGMENT}
  ${ERROR_FRAGMENT}
  ${ORDER_FRAGMENT}
`;

/**
 * 获取微信配置信息 - h5环境
 */
export const fetchGetJsConfigDocument = gql`
  mutation getJsConfig($url: String!) {
    getJsConfig(url: $url) {
      appId
      timestamp
      nonceStr
      signature
    }
  }
`;

/**
 * 下单买家留言
 */
export const fetchNewSetOrderCustomFieldsDocument = gql`
  mutation newSetOrderCustomFields($input: UpdateOrderInput!, $orderId: ID!) {
    newSetOrderCustomFields(input: $input, orderId: $orderId) {
      ...Order
    }
  }
  ${ORDER_FRAGMENT}
`;

/**
 * 创建微信下单
 */
export const fetchCreateWxChatPaymentIntentDocument = gql`
  mutation createWxChatPaymentIntent($paymentType: WeChatPaymentType, $orderId: ID) {
    createWxChatPaymentIntent(paymentType: $paymentType, orderId: $orderId) {
      status
      appId
      timeStamp
      nonceStr
      package
      signType
      paySign
      isSkipPay
    }
  }
`;

/**
 * 查询当前订单中的商品是否参与了会员价活动
 */
export const fetchGetOrderIsIncludeMemberPriceDocument = gql`
  query getOrderIsIncludeMemberPrice($orderId: ID!) {
    getOrderIsIncludeMemberPrice(orderId: $orderId)
  }
`;

/**
 * 查询结算订单中的商品是否都是限购
 */
export const fetchCheckProductCanCheckoutDocument = gql`
  query checkProductCanCheckout($skuIds: [ID!]!) {
    checkProductCanCheckout(skuIds: $skuIds) {
      canCheckout
      reason
    }
  }
`;

/**
 * 取消购物金抵扣
 */
export const fetchCancelShoppingCreditsDocument = gql`
  mutation cancelShoppingCredits($orderId: ID!) {
    cancelShoppingCredits(orderId: $orderId) {
      id
    }
  }
`;

/**
 * 申请购物金抵扣
 */
export const fetchApplyShoppingCreditsDocument = gql`
  mutation applyShoppingCredits($orderId: ID!) {
    applyShoppingCredits(orderId: $orderId) {
      id
    }
  }
`;

/**
 * 查询购物车页面是否有活动倒计时
 */
export const fetchActivityCountdownByCartDocument = gql`
  query activityCountdownByCart {
    activityCountdownByCart {
      activityCountdown {
        id
        createdAt
        activityId
        promotionId
        activityType
        name
        countdownTime
        priority
      }
      activityTag
    }
  }
`;
